import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  FormInput,
  AutocompleteInput,
  AttachmentsZone,
  FormGetter,
  NotificationsContext,
  ConfigurationContext,
  CheckboxInput,
  HasuraSelector,
  Grid,
  FormTabs,
  FormTab,
  HasuraSelectorRef,
  HiddenInput,
  RadioGroupInput,
  useNavigate,
} from '@kirz/mui-admin';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { ContentCopy, FolderOpen, History, Key } from 'mdi-material-ui';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { logsFilterUrl } from 'components/LogsTable';
import { offlineClientsPage } from 'constants/offline-clients-page';
import { colors } from 'theme/colors';

export const ClientsTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const sftpSelectorRef = useRef<HasuraSelectorRef>(null);
    const navigate = useNavigate();
    const { filterColumns, ...rest } = props;
    const { showAlert } = useContext(NotificationsContext);
    const { hasura } = useContext(ConfigurationContext);
    const [directories, setDirectories] = useState<
      { id: string; name: string; emailAccountSuffix?: string }[]
    >([]);
    const tableRef = useRef<DataTableExRef>(null);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            field: 'id',
            headerName: 'ID',
            width: 120,
            sortable: false,
            renderCell: ({ row }) => {
              return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Open map">
                    <IconButton
                      component="a"
                      disabled={!row.company?.bkh}
                      href={`localexplorer:O:\\${row.company?.bkh}`}
                      target="_blank"
                      size="small"
                    >
                      <FolderOpen fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Log">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigate(logsFilterUrl({ client: [row.id] }));
                      }}
                    >
                      <History fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <span>{row.id}</span>
                </Stack>
              );
            },
          },
          {
            field: 'name',
            selector: 'firstName lastName',
            headerName: 'Naam',
            valueGetter({ row }) {
              return `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
            },
            onSort(sort) {
              return [{ firstName: sort }];
            },
            flex: 1,
          },
          {
            field: 'email',
            headerName: 'E-mail',
            flex: 1,
            // type: 'email',
            renderCell({ row }) {
              return (
                <Box
                  component="a"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(row.recipientEmail);
                    showAlert('Copied to clipboard', 'success');
                  }}
                >
                  {row.email}
                </Box>
              );
            },
          },
          {
            field: 'company { *name* }',
            headerName: 'Bedrijf',
            flex: 1,
            type: 'relationship',
            fetchRemoved: false,
          },
          {
            field: 'company { *bkh* }',
            headerName: 'BKH',
            flex: 1,
            type: 'relationship',
            fetchRemoved: false,
          },
          {
            field: 'showInExport',
            headerName: 'Tel.boek',
            flex: 1,
            type: 'boolean',
          },
          {
            field: 'sftpUsername',
            selector:
              'sftpUsername sftpDirectoryStructure sftpOnfactCompanyName sftpOnfactApiKey sftpFacturenInFolder sftpFacturenUitFolder',
            headerName: 'sFTP',
            flex: 1,
            type: 'boolean',
            valueGetter({ row }) {
              return !!row.sftpUsername;
            },
            onSort(sort) {
              return [{ sftpUsername: sort }];
            },
          },
          { field: 'createdAt', headerName: 'Aangemaakt op', type: 'date' },
        ]),
      [filterColumns, navigate],
    );

    useEffect(() => {
      (async () => {
        const items = await hasura.request({
          type: 'query',
          selection: 'id name emailAccountSuffix',
          source: 'directory',
          orderBy: { createdAt: 'DESC' },
        });

        setDirectories(items);
      })();
    }, []);

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="client"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'createdAt', sort: 'desc' }}
        editable={{
          onEdit(row) {
            tableRef.current?.openFormDialog(row);
          },
        }}
        formTitle={(isNew) => (isNew ? 'Nieuwe klant' : 'Bewerk klant')}
        formDialogProps={{
          ...rest.formDialogProps,
          maxWidth: 'md',
          formSubmitterProps: {
            selection: ['id', 'recipientEmail', 'sftpUsername', 'sftpPassword'],
            preSubmit({
              id,
              recipientEmail,
              sftpUsername,
              sftpPassword,
              ...item
            }) {
              return {
                ...item,
                showCompanyUploads: item.showCompanyUploads ?? false,
              };
            },
            async onSubmit(item) {
              if (!item.recipientEmail) {
                await axios.post(`/api/v1/imap/create-account`, {
                  clientId: item.id,
                });

                tableRef.current?.reload();
                return {
                  ...item,
                  isNew: false,
                };
              }

              tableRef.current?.reload();
              return item;
            },
          },
          formProps: {
            defaultValues: {
              showInExport: true,
            },
            grid: false,
          },
        }}
      >
        <HiddenInput name="id" />
        <HiddenInput name="sftpUsername" />
        <HiddenInput name="sftpPassword" />
        <HiddenInput name="sftpDirectoryStructure" />
        <HiddenInput name="sftpOnfactCompanyName" />
        <HiddenInput name="sftpOnfactApiKey" />
        <HiddenInput name="sftpFacturenInFolder" />
        <HiddenInput name="sftpFacturenUitFolder" />
        <HasuraSelector
          selection="sftpUsername sftpPassword"
          ref={sftpSelectorRef}
        />

        <FormTabs
          tabs={[
            { id: 'main', label: 'Algemeen' },
            { id: 'documents', label: 'Documenten' },
            { id: 'sftp', label: 'SFTP' },
          ]}
          tabsWrapperStyle={{
            mx: { xs: 0, md: 0 },
          }}
        >
          <FormTab tab="main">
            <FormInput name="firstName" label="Voornaam" md={3} required />
            <FormInput name="lastName" label="Achternaam" md={3} required />
            <CheckboxInput name="showInExport" label="Tel.boek" md={3} />
            <CheckboxInput
              name="showCompanyUploads"
              label="Permanent dossier"
              md={3}
            />
            <FormInput name="address" label="Adres" md={4} />
            <FormInput name="gsm" label="GSM" md={4} />
            <HasuraSelector selection="recipientEmail sftpUsername sftpPassword" />
            <Grid md={4}>
              <FormGetter
                names={['recipientEmail']}
                render={({ recipientEmail }) => (
                  <Button
                    sx={{ mt: '6px' }}
                    startIcon={<ContentCopy />}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        directories
                          .filter((x) => x.emailAccountSuffix)
                          .map(
                            (x) =>
                              `${x.name}: ${recipientEmail.replace(
                                '@',
                                `_${x.emailAccountSuffix}@`,
                              )}`,
                          )
                          .join('\n'),
                      );
                      showAlert('Gekopieerd', 'success');
                    }}
                  >
                    Copy E-mail MP
                  </Button>
                )}
              />
            </Grid>

            <FormInput name="phone" label="Telefoon" md={4} />
            <FormInput name="email" label="E-mail" md={4} />
            <AutocompleteInput
              name="companyId"
              source="company"
              mode="hasura"
              selection="id name"
              label="Bedrijf"
              itemText="name"
              itemValue="id"
              md={4}
            />
            <FormInput name="nationalRegister" label="Rijksregister" md={8} />
            <AutocompleteInput
              name="pb"
              mode="memory"
              label="PB"
              options={[
                { value: 'geen', text: 'Geen' },
                { value: 'niet-complex', text: 'Niet-complex' },
                { value: 'complex', text: 'Complex' },
              ]}
              required
              md={4}
            />
          </FormTab>
          <FormTab tab="sftp">
            <FormGetter
              names={['id', 'sftpUsername', 'sftpPassword']}
              render={({ id, sftpUsername, sftpPassword }) => {
                if (!id) return null;

                if (!sftpUsername) {
                  return (
                    <Grid xs={12}>
                      <Button
                        variant="outlined"
                        startIcon={<Key />}
                        onClick={async () => {
                          try {
                            await axios.post(
                              `/api/v1/clients/${id}/generate-sftp`,
                            );
                            showAlert(
                              'SFTP-inloggegevens gegenereerd',
                              'success',
                            );
                            sftpSelectorRef.current?.refetch();
                          } catch {
                            showAlert(
                              'Kon SFTP-inloggegevens niet genereren',
                              'error',
                            );
                          }
                        }}
                      >
                        Genereer SFTP-inloggegevens
                      </Button>
                    </Grid>
                  );
                }

                return (
                  <>
                    <RadioGroupInput
                      name="sftpDirectoryStructure"
                      label="Directory structure"
                      items={[
                        { text: 'OnFact', value: 'onfact' },
                        { text: 'Other', value: 'other' },
                      ]}
                      required
                      row
                    />
                    <FormGetter
                      names={['sftpDirectoryStructure']}
                      render={({ sftpDirectoryStructure }) => {
                        if (sftpDirectoryStructure === 'onfact') {
                          return (
                            <>
                              <FormInput
                                name="sftpOnfactCompanyName"
                                label="OnFact bedrijfsnaam"
                                required
                              />
                              <FormInput
                                name="sftpOnfactApiKey"
                                label="OnFact API-sleutel"
                                // required
                              />
                            </>
                          );
                        }

                        return (
                          <>
                            <FormInput
                              name="sftpFacturenInFolder"
                              label="Directory sync with facturen_in"
                              placeholder="/Rute98BV/<year>/Facturen/in"
                              xs={12}
                            />
                            <FormInput
                              name="sftpFacturenUitFolder"
                              label="Directory sync with facturen_uit"
                              placeholder="/Rute98BV/<year>/Facturen/uit"
                              xs={12}
                            />
                            <FormInput
                              name="sftpCreditnotasUitFolder"
                              label="Directory sync with creditnotas_uit"
                              placeholder="/Rute98BV/<year>/Creditnotas/uit"
                              xs={12}
                            />
                            <Grid xs={12}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ mt: -1, display: 'block' }}
                              >
                                Je kunt {'<year>'} gebruiken voor het huidige
                                jaar. Het pad moet beginnen met "/" en elke map
                                moet worden gescheiden door "/".
                              </Typography>
                            </Grid>
                          </>
                        );
                      }}
                    />

                    <Grid xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          // bgcolor: 'grey.100',
                          borderRadius: 1,
                          border: `1px solid ${colors.border}`,
                          fontFamily: 'monospace',
                        }}
                      >
                        <Button
                          size="small"
                          startIcon={<ContentCopy />}
                          variant="contained"
                          sx={{ mb: 2 }}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `Protocol: sFTP\nHostnaam: 54.37.87.131\nPoort: 22822\nGebruikersnaam: ${sftpUsername}\nWachtwoord: ${sftpPassword}\nHoofdmap: /upload`,
                            );
                            showAlert(
                              'SFTP-inloggegevens gekopieerd',
                              'success',
                            );
                          }}
                        >
                          Kopieer inloggegevens
                        </Button>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Protocol: sFTP</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Hostnaam:</strong> 54.37.87.131
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Poort:</strong> 22822
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Gebruikersnaam:</strong> {sftpUsername}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Wachtwoord:</strong> {sftpPassword}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Hoofdmap:</strong> /upload
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontStyle: 'italic' }}
                        >
                          Alleen de "/upload" map is beschikbaar voor uploads
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                );
              }}
            />
          </FormTab>
          <FormTab tab="documents">
            <FormGetter
              names={['id']}
              render={(x) => {
                return (
                  !!x.id && (
                    <AttachmentsZone
                      name="clientId"
                      entityId={x.id}
                      title="Documenten"
                      // readOnly
                      attachmentsTypes={[
                        ...directories.map((x) => ({
                          value: x.name,
                          text: x.name,
                        })),
                      ]}
                    />
                  )
                );
              }}
            />
          </FormTab>
        </FormTabs>
      </DataTableEx>
    );
  },
);
